import { useRouter } from "@/_router";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef, memo, useCallback } from "react";
import { formatDuration } from "@/core";
import { LANGUAGES } from "@/languages";
import { useAppStore } from "@/controller";
import { useAudioPlayer } from "@/models/useAudioPlayer";
import { logClientEvent } from "@/telemetry";
import { recordFeatureUsed } from "@/models/feature-adoption";
import { useStillWorking } from "@/models/useStillWorking";
import Container from "@/components/Container";
import PageHeader from "@/components/PageHeader";
import BackgroundAmbience from "@/components/BackgroundAmbience";
import BottomActionBar from "@/components/BottomActionBar";
import CircularProgress from "@/components/CircularProgress";
import {
  CopyIcon,
  DownloadIcon,
  PlayIcon,
  PauseIcon,
} from "@/components/Icons";
import { Transcript } from "@/types";
import { getContentDirection, type ContentDirection } from "@/text-direction";
import { actions, runtime } from "@/controller";
import { useUISlice } from "@/models/useUISlice";
import {
  exportTranscript,
  transcriptPlainText,
} from "@/transcript-export";
import toast from "react-hot-toast";

const getEstimatedTranslationDurationMs = (characterCount: number) => {
  return Math.min(90_000, Math.max(15_000, 15_000 + characterCount * 3.5));
};

// ─── Segment Row ────────────────────────────────────────────

const SegmentRow = memo(({
  segment,
  isActive,
  isPlaying,
  isAudioFile,
  hasAudio,
  onPlaySegment,
  onPauseAudio,
  direction,
}: {
  segment: Transcript["segments"][number];
  isActive: boolean;
  isPlaying: boolean;
  isAudioFile: boolean;
  hasAudio: boolean;
  onPlaySegment: (startTime: number) => void;
  onPauseAudio: () => void;
  direction: ContentDirection;
}) => {
  const handlePlayPause = () => {
    if (isActive && isPlaying) {
      onPauseAudio();
    } else {
      onPlaySegment(segment.start);
    }
  };

  return (
    <div
      className={`py-4 first:pt-0 transition-all duration-200 rounded-2xl ${
        isActive ? "-mx-3 px-3" : ""
      }`}
      style={
        isActive
          ? {
              backgroundColor: "var(--highlight-bg)",
              boxShadow: "inset 0 0 0 1px var(--highlight-border)",
            }
          : undefined
      }
    >
      <div className="flex items-center gap-2.5 mb-2">
        {hasAudio && (
          <button
            onClick={handlePlayPause}
            data-action-id="translate.segmentPlayback"
            className="icon-btn w-9 h-9 active:scale-90 transition-all shrink-0"
          >
            {isActive && isPlaying ? (
              <PauseIcon size={12} className="text-[var(--text-primary)]" />
            ) : (
              <PlayIcon
                size={12}
                className="text-[var(--text-primary)] ml-0.5"
              />
            )}
          </button>
        )}
        {isAudioFile && (
          <span
            dir="ltr"
            className="text-[12px] text-[var(--text-muted)] tabular-nums font-medium"
          >
            {formatDuration(segment.start)}
          </span>
        )}
      </div>
      <p
        dir={direction}
        onClick={hasAudio ? () => onPlaySegment(segment.start) : undefined}
        data-action-id="translate.segmentPlayback"
        className={`text-[17px] leading-[1.7] text-[var(--text-primary)] transition-colors duration-200 ${
          hasAudio ? "cursor-pointer active:opacity-60 hover:opacity-80" : ""
        }`}
      >
        {segment.text}
      </p>
    </div>
  );
});

// ─── Main Component ─────────────────────────────────────────

const WebTranslate = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const transcriptId = new URLSearchParams(window.location.search).get("id");
  const transcript = useAppStore((state) =>
    state.transcripts.find((transcript) => transcript.id === transcriptId)
  );

  const getTranslateLanguage = () => {
    if (typeof window === "undefined") return "en";
    const params = new URLSearchParams(window.location.search);
    return params.get("lang") || "en";
  };

  const getTranslateLanguageLabel = () => {
    const code = getTranslateLanguage();
    return LANGUAGES.find((l) => l.value === code)?.label || code;
  };

  const translateLanguage = getTranslateLanguage();
  const translateLanguageLabel = getTranslateLanguageLabel();
  const translatedSegments = transcript?.translation?.[translateLanguage];
  const currentTranscriptId = transcript?.id;
  const sourceCharacterCount =
    transcript?.segments?.reduce(
      (total, segment) => total + (segment.text?.length || 0),
      0
    ) || 0;

  const [loading, setLoading] = useState(!translatedSegments);
  const stillWorking = useStillWorking(loading);


  const [progress, setProgress] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const displayProgress = Math.round(progress);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const generationHandleRef = useRef<ReturnType<
    typeof actions.aiContent.generate
  > | null>(null);
  const generationIdRef = useRef(0);

  const {
    audioRef,
    isPlaying: audioIsPlaying,
    playbackRate,
    hasAudio,
    isAudioFile,
    activeSegmentIndex,
    togglePlayPause,
    playFromTime: handlePlaySegment,
    pause: handlePauseAudio,
    toggleSpeed,
  } = useAudioPlayer(
    transcript
      ? { ...transcript, segments: translatedSegments || [] }
      : undefined
  );

  const _startProgress = useCallback((characterCount: number) => {
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    const estimatedDurationMs =
      getEstimatedTranslationDurationMs(characterCount);
    const progressIncrement = (99 * 1_000) / estimatedDurationMs;
    setProgress(0);
    intervalIdRef.current = setInterval(() => {
      setProgress((prev) => Math.min(99, prev + progressIncrement));
    }, 1000);
  }, []);

  useEffect(() => {
    if (transcriptId && !transcript) {
      void actions.transcript.get(transcriptId);
    }
  }, [transcriptId, transcript]);

  useEffect(() => {
    setIsScrolled(false);
    setProgress(0);
    setLoading(!translatedSegments);
  }, [transcriptId, translateLanguage, translatedSegments]);

  const generateTranslation = useCallback(async () => {
    if (!currentTranscriptId) return;
    generationIdRef.current += 1;
    const generationId = generationIdRef.current;
    generationHandleRef.current?.cancel();
    setLoading(true);
    _startProgress(sourceCharacterCount);
    const handle = actions.aiContent.generate({
      feature: "translation",
      transcriptId: currentTranscriptId,
      language: translateLanguage,
    });
    generationHandleRef.current = handle;
    const completed = await handle.done;
    if (generationId !== generationIdRef.current) return;
    generationHandleRef.current = null;
    if ("error" in completed) setLoading(false);
  }, [currentTranscriptId, sourceCharacterCount, translateLanguage, _startProgress]);

  useEffect(() => {
    if (translatedSegments) {
      setLoading(false);
      return;
    }
    if (currentTranscriptId) void generateTranslation();
    return () => {
      generationIdRef.current += 1;
      generationHandleRef.current?.cancel();
      generationHandleRef.current = null;
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    };
  }, [
    currentTranscriptId,
    Boolean(translatedSegments),
    translateLanguage,
    generateTranslation,
  ]);

  useEffect(() => {
    if (translatedSegments && loading) {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      setLoading(false);
      setProgress(100);
      recordFeatureUsed("translate");
    }
  }, [translatedSegments, loading, translateLanguage]);

  const _retry = async () => {
    await generateTranslation();
  };

  const scrollRafRef = useRef<number | null>(null);
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      setIsScrolled(scrollTop > 10);
      scrollRafRef.current = null;
    });
  }, []);

  // Loading State
  if (loading) {
    return (
      <Container reading className="fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden">
        <BackgroundAmbience />
        <PageHeader
          isScrolled={false}
          onBack={() => router.back()}
        />
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-20">
          <CircularProgress
            progress={progress}
            size="lg"
            indeterminate={stillWorking}
          />
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 mt-6 tracking-tight">
            {t("translate.translationTo", { language: translateLanguageLabel })}
          </h3>
          <p className="text-[14px] text-[var(--text-tertiary)] leading-relaxed">
            {stillWorking
              ? t("aiContent.stillWorkingSubtitle")
              : t("translate.generatingTranslation", {
                  progress: displayProgress,
                })}
          </p>
        </div>
      </Container>
    );
  }

  // Error State
  if (!translatedSegments && !loading) {
    return (
      <Container reading className="fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden">
        <BackgroundAmbience />
        <PageHeader
          isScrolled={false}
          onBack={() => router.back()}
        />
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-20">
          <div className="w-20 h-20 rounded-full bg-[var(--destructive)]/10 flex items-center justify-center mb-6">
            <svg
              width={32}
              height={32}
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--destructive)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 tracking-tight">
            {t("translate.translationFailedDescription")}
          </h3>
          <button
            onClick={_retry}
            data-action-id="translate.regenerate"
            className="btn btn-primary mt-8 w-full max-w-[280px] text-[15px] items-center justify-center active:scale-[0.97] transition-all"
          >
            {t("translate.tryAgain")}
          </button>
        </div>
      </Container>
    );
  }

  if (!translatedSegments) {
    throw new Error("Translation success state requires translated segments");
  }
  const completedSegments = translatedSegments;

  // Success State
  return (
    <Container reading className="fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden selection:bg-[var(--accent)]/20">
      <BackgroundAmbience />

      <PageHeader
        isScrolled={isScrolled}
        onBack={() => router.back()}
      >
        {hasAudio && (
          <button
            onClick={togglePlayPause}
            data-action-id="translate.playback.toggle"
            className="glass h-11 px-4 rounded-full flex items-center justify-center active:scale-90 transition-all text-[var(--text-primary)] min-w-[3rem]"
          >
            {audioIsPlaying ? (
              <PauseIcon size={15} />
            ) : (
              <PlayIcon size={15} className="ml-0.5" />
            )}
          </button>
        )}
        {hasAudio && (
          <button
            dir="ltr"
            onClick={toggleSpeed}
            data-action-id="translate.playback.speed"
            className="glass h-11 px-4 rounded-full flex items-center justify-center active:scale-90 transition-all text-[var(--text-primary)] text-[13px] font-semibold tabular-nums min-w-[3rem]"
          >
            {playbackRate}x
          </button>
        )}
      </PageHeader>

      <main
        className="flex-1 relative z-10 overflow-y-auto"
        onScroll={handleScroll}
      >
        <div className="px-5 md:px-8 max-w-3xl mx-auto">
        {hasAudio && (
          <audio
            ref={audioRef}
            src={transcript!.originalFileUrl!}
            preload="metadata"
          />
        )}

        <div className="pt-4 pb-5">
          <h1 className="text-[26px] font-bold text-[var(--text-primary)] leading-tight tracking-tight mb-1">
            {t("translate.translationTo", { language: translateLanguageLabel })}
          </h1>
          <p dir="auto" className="text-[14px] text-[var(--text-muted)]">
            {transcript?.fileName}
          </p>
        </div>

        <div className="pb-44">
          {completedSegments.map((segment, idx) => (
            <SegmentRow
              key={`${segment.start}-${idx}`}
              segment={segment}
              isActive={idx === activeSegmentIndex}
              isPlaying={audioIsPlaying}
              isAudioFile={isAudioFile}
              hasAudio={hasAudio}
              onPlaySegment={handlePlaySegment}
              onPauseAudio={handlePauseAudio}
              direction={getContentDirection(translateLanguage)}
            />
          ))}
        </div>
        </div>
      </main>

      <BottomActionBar compactBottom>
        <div className="mx-auto flex w-fit max-w-full items-center justify-center gap-2.5">
          <button
            onClick={async () => {
              logClientEvent("translation.copied", {
                language: translateLanguageLabel,
              });
              if (transcript && transcript.id) {
                await runtime.clipboard.write(
                  transcriptPlainText({
                    ...transcript,
                    segments: completedSegments,
                  })
                );
                toast.success(t("actions.copySuccess"));
              }
            }}
            data-action-id="translate.copy"
            className="btn btn-primary shrink-0 gap-2 px-5 text-[14px] active:scale-[0.97] transition-all"
          >
            <CopyIcon size={18} />
            {t("translate.copy")}
          </button>
          <button
            onClick={() => {
              if (transcript && transcript.id) {
                logClientEvent("translation.exportSelected", {
                  language: translateLanguageLabel,
                });
                const translatedTranscript = {
                  ...transcript,
                  language: translateLanguage,
                  segments: completedSegments,
                };
                useUISlice.getState().setAlertExportTranscriptOptions({
                  hasSpeakers: completedSegments.some(
                    (segment) => segment.speaker
                  ),
                  onExport: (options) =>
                    exportTranscript(translatedTranscript, options),
                  });
              }
            }}
            data-action-id="translate.download"
            className="btn btn-glass shrink-0 gap-2 px-5 text-[14px] active:scale-[0.97] transition-all"
          >
            <DownloadIcon size={18} />
            {t("translate.download")}
          </button>
        </div>
      </BottomActionBar>
    </Container>
  );
};

export default WebTranslate;
